import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { SchoolActivity } from '../../../models/SchoolActivity';
import { SchoolActivityService } from '../../../services/school-activity.service';
import { ToastService } from '../../../services/toast.service';
import {
	AddSchoolActivityAction,
	AddSchoolActivityFailureAction,
	AddSchoolActivitySuccessAction,
	DeleteSchoolActivityFailureAction,
	DeleteSchoolActivitySuccessAction,
	GetSchoolActivitiesByStatusFailureAction,
	GetSchoolActivitiesByStatusSuccessAction,
	GetSchoolActivitiesFailureAction,
	GetSchoolActivitiesSuccessAction,
	GetSchoolActivityByIdFailureAction,
	GetSchoolActivityByIdSuccessAction,
	SchoolActivitiesActionTypes,
	UpdateSchoolActivityAction,
	UpdateSchoolActivityFailureAction,
	UpdateSchoolActivitySuccessAction,
} from '../actions/school-activities.actions';

@Injectable()
export class SchoolActivitiesEffects {
	addSchoolActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(AddSchoolActivityAction),
			exhaustMap(({ createReq }) =>
				this.schoolActivityService.CreateActivityHTTP(createReq).pipe(
					switchMap((newActivity) => {
						this.toastService.openToast({ title: `Activity successfully created!`, type: 'success' });

						this.router.navigate(['/main/flex/activities', newActivity.id]);
						return of(AddSchoolActivitySuccessAction({ schoolActivity: newActivity }));
					}),
					catchError((error) => {
						this.toastService.openToast({ title: `Error while creating activity, try again.`, type: 'error' });
						return of(AddSchoolActivityFailureAction({ error: error.message }));
					})
				)
			)
		);
	});

	loadSchoolActivities$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(SchoolActivitiesActionTypes.GetSchoolActivities),
			exhaustMap(({ flex_period_id }) => {
				return this.schoolActivityService.GetActivitiesHTTP({ flex_period_id }).pipe(
					map((schoolActivities: SchoolActivity[]) => GetSchoolActivitiesSuccessAction({ schoolActivities })),
					catchError((error) => of(GetSchoolActivitiesFailureAction({ errorMessage: error })))
				);
			})
		);
	});

	loadSchoolActivitiesByStatus$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(SchoolActivitiesActionTypes.GetSchoolActivitiesByStatus),
			exhaustMap(({ req, setAll }) => {
				return this.schoolActivityService.GetActivitiesByStatusHTTP(req).pipe(
					map((schoolActivities: SchoolActivity[]) =>
						GetSchoolActivitiesByStatusSuccessAction({
							schoolActivities,
							setAll: setAll,
						})
					),
					catchError((error) => of(GetSchoolActivitiesByStatusFailureAction({ errorMessage: error })))
				);
			})
		);
	});

	getSchoolActivityById$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(SchoolActivitiesActionTypes.GetSchoolActivityById),
			exhaustMap(({ activityId, includeVisibilites }) => {
				return this.schoolActivityService.GetActivityByIdHTTP(activityId, includeVisibilites).pipe(
					map((schoolActivity: SchoolActivity) => {
						return GetSchoolActivityByIdSuccessAction({ schoolActivity });
					}),
					catchError((error) => of(GetSchoolActivityByIdFailureAction({ errorMessage: error })))
				);
			})
		);
	});

	updateSchoolActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(UpdateSchoolActivityAction),
			exhaustMap(({ req }) => {
				return this.schoolActivityService.UpdateActivityHTTP(req).pipe(
					map((schoolActivity: SchoolActivity) => {
						return UpdateSchoolActivitySuccessAction({ schoolActivity, fromLiveUpdate: false });
					}),
					catchError((error) => of(UpdateSchoolActivityFailureAction({ errorMessage: error })))
				);
			})
		);
	});

	deleteSchoolActivity$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(SchoolActivitiesActionTypes.DeleteSchoolActivity),
			exhaustMap(({ activityId }) => {
				return this.schoolActivityService.DeleteActivityHTTP(activityId).pipe(
					map(() => {
						this.toastService.openToast({ title: `Activity removed`, type: 'success' });
						return DeleteSchoolActivitySuccessAction({ activityId });
					}),
					catchError((error) => {
						this.toastService.openToast({
							title: `Issue encountered while deleting activity, try again`,
							type: 'error',
						});
						return of(DeleteSchoolActivityFailureAction({ errorMessage: error }));
					})
				);
			})
		);
	});

	constructor(
		private actions$: Actions,
		private schoolActivityService: SchoolActivityService,
		private toastService: ToastService,
		private router: Router
	) {}
}
